import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Play, FileText, Image as ImageIcon, Music, Link as LinkIcon, Mic, Brain, BookOpen, Youtube, Bookmark, Sparkles, Smartphone, Mail } from 'lucide-react';
import { useInstalledTWA, buildTWAIntentUrl } from '../hooks/useInstalledTWA';
import EmailAuthSheet from '../components/EmailAuthSheet';

// AppLanding - minimal premium landing shown to standalone (TWA / installed
// PWA) users. Web visitors at brainsync.pro keep the marketing landing page
// for SEO + paid acquisition.
//
// Visual: phone mockup centered on top, bold "REMEMBER EVERYTHING." headline
// below, chunky content icons fly in from the edges, land on the dashboard
// cards, and dissolve with a blur fade - like the phone is absorbing them.

const FlyingIcon = ({ icon: Icon, fromX, fromY, color, delay }) => (
    <motion.div
        initial={{ opacity: 0, x: fromX, y: fromY, scale: 0.65, rotate: -12, filter: 'blur(0px)' }}
        animate={{
            // The icon stays fully visible until it has visually entered
            // the phone (lands ~30px below overlay center, which corresponds
            // to the dashboard cards area). The dissolve happens entirely
            // in the last ~12% of the cycle so the absorption reads clearly.
            opacity: [0, 1, 1, 1, 0],
            x: [fromX, fromX * 0.55, fromX * 0.18, 0, 0],
            y: [fromY, fromY * 0.55, fromY * 0.18 + 20, 30, 50],
            scale: [0.65, 1, 1, 0.85, 0.15],
            rotate: [-12, 0, 4, 6, 18],
            filter: ['blur(0px)', 'blur(0px)', 'blur(0px)', 'blur(2px)', 'blur(10px)'],
        }}
        transition={{
            duration: 3.6,
            times: [0, 0.3, 0.78, 0.88, 1],
            delay,
            repeat: Infinity,
            repeatDelay: 0.5,
            ease: [0.22, 1, 0.36, 1],
        }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-2xl flex items-center justify-center shadow-[0_12px_28px_-6px_rgba(0,0,0,0.32)]"
        style={{ background: color }}
        aria-hidden="true"
    >
        <Icon className="w-5 h-5 text-white" strokeWidth={2.2} />
    </motion.div>
);

// Detailed mock card matching the reference image: bigger, bolder, with
// platform-coloured icon, snippet text under the title, prominent tags,
// and a date/byline footer.
const MockCard = ({ icon: Icon, iconBg, iconColor, title, snippet, tags, badge, sparkle, rotate, delay }) => (
    <motion.div
        initial={{ opacity: 0, y: 18, rotate: rotate - 3 }}
        animate={{ opacity: 1, y: 0, rotate }}
        transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
        className="bg-white rounded-2xl shadow-[0_8px_24px_-8px_rgba(0,0,0,0.14)] border border-gray-100 p-2.5 relative"
    >
        {sparkle && (
            <Sparkles className="absolute top-1.5 right-2 w-3 h-3 text-amber-400" aria-hidden="true" />
        )}
        {badge && (
            <span className="absolute -top-1.5 left-2 bg-emerald-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                {badge}
            </span>
        )}
        <div className="flex gap-2 items-start">
            <div
                className="w-10 h-10 rounded-xl shrink-0 flex items-center justify-center"
                style={{ background: iconBg }}
            >
                <Icon className="w-5 h-5" style={{ color: iconColor }} fill={iconColor} strokeWidth={iconColor === '#FFFFFF' ? 0 : 1.5} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[12px] font-bold text-[#1A1F1B] leading-tight">{title}</div>
                {snippet && (
                    <div className="text-[9px] text-gray-500 mt-0.5 leading-tight line-clamp-2">{snippet}</div>
                )}
                <div className="flex gap-1 mt-1.5 flex-wrap">
                    {tags.map((t) => (
                        <span key={t} className="text-[9px] font-medium px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-700">
                            {t}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    </motion.div>
);

const PhoneMockup = () => (
    // Phone scales with viewport height so the entire hero (phone + text +
    // CTA) fits in one screen even on shorter iPhone viewports. On a
    // Pixel/desktop with plenty of vertical room we cap at 232px.
    <div className="relative" style={{ width: 'clamp(178px, 28dvh, 232px)' }}>
        {/* Flying icons overlay */}
        <div className="absolute inset-0 pointer-events-none z-20" aria-hidden="true">
            <FlyingIcon icon={Play}      fromX={-150} fromY={-150} color="#EF4444" delay={0} />
            <FlyingIcon icon={FileText}  fromX={ 160} fromY={-130} color="#10b981" delay={0.4} />
            <FlyingIcon icon={ImageIcon} fromX={-170} fromY={  60} color="#A855F7" delay={0.8} />
            <FlyingIcon icon={Music}     fromX={ 150} fromY={ 100} color="#F59E0B" delay={1.2} />
            <FlyingIcon icon={LinkIcon}  fromX={-100} fromY={-210} color="#3B82F6" delay={1.6} />
            <FlyingIcon icon={Mic}       fromX={ 120} fromY={ 180} color="#EC4899" delay={2.0} />
            <FlyingIcon icon={BookOpen}  fromX={-200} fromY={ -60} color="#14B8A6" delay={2.4} />
        </div>

        <motion.div
            initial={{ opacity: 0, y: 30, rotate: -8 }}
            animate={{ opacity: 1, y: 0, rotate: -6 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto"
            style={{ width: '100%' }}
        >
            <div
                className="rounded-[36px] p-2 shadow-[0_30px_60px_-20px_rgba(46,58,47,0.45)]"
                style={{ background: '#1A1F1B' }}
            >
                <div className="rounded-[28px] overflow-hidden bg-[#FDFCF8] aspect-[9/19] relative">
                    {/* Notch */}
                    <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-4 rounded-full bg-[#1A1F1B] z-10" />

                    <div className="pt-9 px-2.5">
                        <div className="flex items-center justify-between mb-2">
                            <div className="w-7 h-7 rounded-lg bg-[#2E3A2F] flex items-center justify-center">
                                <Brain className="w-3.5 h-3.5 text-white" />
                            </div>
                            <div className="text-[10px] text-gray-500 font-medium">All · 142</div>
                        </div>
                        <div className="bg-gray-100/80 rounded-full px-3 py-1.5 mb-2.5 text-[10px] text-gray-400">
                            AI search…
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <MockCard
                                icon={Youtube}
                                iconBg="#FEE2E2"
                                iconColor="#EF4444"
                                title="The Neuroscience of Learning"
                                snippet="…rewiring neural pathways for better retention…"
                                tags={['brain', 'learning']}
                                sparkle
                                rotate={-1}
                                delay={0.4}
                            />
                            <MockCard
                                icon={Bookmark}
                                iconBg="#FED7AA"
                                iconColor="#EA580C"
                                title="Building Your Second Brain"
                                snippet="…techniques for integrating digital notes…"
                                tags={['productivity', 'PKM']}
                                rotate={1}
                                delay={0.55}
                            />
                            <MockCard
                                icon={Mic}
                                iconBg="#E9D5FF"
                                iconColor="#9333EA"
                                title="How to Structure Complex Info"
                                snippet="…building mental models that stick."
                                tags={['podcast', 'focus']}
                                badge="New"
                                rotate={-0.5}
                                delay={0.7}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    </div>
);

const AppLanding = ({ onSignIn, embedded = false }) => {
    const [emailSheetOpen, setEmailSheetOpen] = useState(false);
    const [emailSheetMode, setEmailSheetMode] = useState('register');
    // If the BrainSync TWA is already installed on this device (mobile Chrome
    // case), we hand the user off to the app via an Android intent URL
    // instead of starting a web OAuth flow. Without this, Android intercepts
    // Google's redirect back to brainsync.pro and the Chrome tab is left
    // stuck on Google's "Choose an account" loader while the TWA logs in.
    const twaInstalledPackage = useInstalledTWA();
    const twaInstalled = Boolean(twaInstalledPackage);

    // UA-based fallback: `navigator.getInstalledRelatedApps()` is unreliable
    // (Chrome may take up to 24h post-install to recognise the TWA, asset
    // link verification timing, manifest cache, etc.). For any Android Chrome
    // user we ALWAYS expose a secondary "Have the app? Open BrainSync" link
    // - a guaranteed escape hatch from the OAuth-redirect-hijack problem,
    // even when auto-detection returns empty. Targets the Play Store
    // package first (com.brainsync.myapp) with the legacy package as
    // fallback. The intent's S.browser_fallback_url drops non-TWA users
    // back to brainsync.pro instead of erroring out.
    const isAndroidChrome =
        typeof navigator !== 'undefined'
        && /Android/i.test(navigator.userAgent)
        && /Chrome/i.test(navigator.userAgent)
        && !/wv\)/i.test(navigator.userAgent); // exclude WebViews (TWA itself)

    const openInApp = () => {
        const pkg = twaInstalledPackage || 'com.brainsync.myapp';
        window.location.href = buildTWAIntentUrl('/dashboard', pkg);
    };

    // Pending-share preview: if the user landed here from a share intent
    // while logged out, the Dashboard's share handler stashed the URL in
    // sessionStorage before kicking them to this page. Show a small banner
    // so they understand WHY we're asking them to sign in.
    const pendingShare = useMemo(() => {
        try {
            const raw = sessionStorage.getItem('bs_pending_share');
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (!parsed || !parsed.ts) return null;
            // Same 30-minute freshness window as the Dashboard consumer.
            if (Date.now() - parsed.ts > 30 * 60 * 1000) return null;
            const link = parsed.url || parsed.text || '';
            if (!link) return null;
            let host = '';
            try { host = new URL(link).hostname.replace(/^www\./, ''); } catch (_) { host = link; }
            return { link, host };
        } catch (_) { return null; }
    }, []);

    const handlePrimaryAction = () => {
        if (twaInstalled) {
            // Cleanly deep-link into the TWA the user actually has installed
            // (Play Store `com.brainsync.myapp` or legacy `com.brainsync.app`).
            // Chrome navigates away to the app immediately - no spinning tab.
            openInApp();
            return;
        }
        onSignIn();
    };
    return (
        <div
            className="w-full overflow-x-hidden flex flex-col relative"
            style={{
                // Use dvh (dynamic viewport) so the hero fits the visible
                // area on iOS Safari (which has a collapsible address bar).
                // svh would underuse space when the bar is collapsed; dvh
                // gives us "whatever is currently visible".
                minHeight: '100dvh',
                height: embedded ? 'auto' : '100dvh',
                background:
                    'radial-gradient(ellipse 80% 60% at 50% 0%, #DCFCE7 0%, transparent 60%), #FDFCF8',
                paddingTop: embedded ? '0' : 'env(safe-area-inset-top)',
                paddingBottom: embedded ? '0' : 'env(safe-area-inset-bottom)',
            }}
            data-testid="app-landing"
        >
            {/* Grain texture for depth */}
            <div
                className="fixed inset-0 pointer-events-none opacity-[0.03] mix-blend-multiply"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
                }}
                aria-hidden="true"
            />

            {/* Hero - phone mockup centered in the upper space */}
            <div className="flex-1 flex flex-col items-center justify-center px-6 pt-6 pb-2 relative z-10">
                <PhoneMockup />
            </div>

            {/* Headline + CTA - spacing scales down on shorter screens */}
            <div className="px-6 pt-5 pb-8 relative z-10">
                {pendingShare && (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                        className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50/80 backdrop-blur px-3.5 py-2.5 flex items-start gap-2.5"
                        data-testid="pending-share-banner"
                    >
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0 mt-0.5">
                            <LinkIcon className="w-4 h-4 text-emerald-700" strokeWidth={2.2} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[12px] font-semibold text-emerald-900 leading-tight">
                                Sign in to save this link
                            </div>
                            <div className="text-[11px] text-emerald-800/70 truncate mt-0.5" title={pendingShare.link}>
                                {pendingShare.host}
                            </div>
                        </div>
                    </motion.div>
                )}
                <motion.h1
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="font-heading font-black uppercase text-[#2E3A2F] leading-[0.9] tracking-tight mb-2.5"
                    style={{ fontSize: 'clamp(30px, 8.5vw, 48px)', letterSpacing: '-0.035em' }}
                >
                    Remember<br />
                    <span className="text-emerald-600">everything.</span>
                </motion.h1>
                <motion.p
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="text-[14px] sm:text-[15px] text-gray-600 leading-snug mb-5"
                >
                    BrainSync is an AI bookmark manager that transcribes videos, summarizes articles and lets you ask questions across everything you save.
                </motion.p>

                <motion.button
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    onClick={handlePrimaryAction}
                    data-testid={twaInstalled ? 'app-landing-open-app-btn' : 'app-landing-google-signin-btn'}
                    className="w-full bg-[#2E3A2F] text-white rounded-full h-[52px] flex items-center justify-center gap-3 font-semibold text-[15px] tracking-tight shadow-[0_12px_30px_-10px_rgba(46,58,47,0.55)] active:scale-[0.98] transition-transform"
                >
                    {twaInstalled ? (
                        <>
                            <Smartphone className="w-5 h-5" strokeWidth={2.2} aria-hidden="true" />
                            Open in BrainSync App
                        </>
                    ) : (
                        <>
                            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                                <path fill="#FFFFFF" d="M21.35 11.1H12v2.8h5.35c-.23 1.5-1.7 4.4-5.35 4.4-3.22 0-5.85-2.67-5.85-5.96s2.63-5.95 5.85-5.95c1.84 0 3.07.79 3.77 1.46l2.57-2.47C16.71 3.95 14.6 3 12 3 6.98 3 3 6.98 3 12s3.98 9 9 9c5.2 0 8.64-3.65 8.64-8.79 0-.59-.06-1.04-.14-1.51z"/>
                            </svg>
                            Continue with Google
                        </>
                    )}
                </motion.button>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.75 }}
                    className="text-center text-[11px] text-gray-400 mt-3 tracking-wide uppercase"
                >
                    {twaInstalled
                        ? 'App detected · Tap to continue inside BrainSync'
                        : 'No credit card · Free forever for basics'}
                </motion.p>

                {/* Email/password escape hatch - secondary, deliberately
                    quieter than the Google CTA. Hidden when the TWA is
                    installed (we want those users in the app, not
                    sideways into a web form). Two compact links: "Sign
                    up with email" and "Sign in" - same surface, just
                    different starting mode. */}
                {!twaInstalled && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.85 }}
                        className="flex items-center justify-center gap-2 mt-4 text-[12.5px]"
                    >
                        <button
                            type="button"
                            onClick={() => { setEmailSheetMode('register'); setEmailSheetOpen(true); }}
                            className="inline-flex items-center gap-1.5 text-gray-600 hover:text-emerald-700 font-medium"
                            data-testid="app-landing-email-signup-link"
                        >
                            <Mail className="w-3.5 h-3.5" />
                            Sign up with email
                        </button>
                        <span className="text-gray-300">·</span>
                        <button
                            type="button"
                            onClick={() => { setEmailSheetMode('login'); setEmailSheetOpen(true); }}
                            className="text-gray-600 hover:text-emerald-700 font-medium"
                            data-testid="app-landing-email-signin-link"
                        >
                            Sign in
                        </button>
                    </motion.div>
                )}

                {/* Android Chrome escape hatch - visible whenever we're on
                    Android Chrome AND the auto-detected TWA badge isn't
                    already the primary CTA. Manual fallback for users whose
                    `getInstalledRelatedApps()` returns empty (Play Store
                    install verification delay, etc.). Tapping fires the
                    same intent URL; if no TWA is installed, Chrome falls
                    back to the Play Store listing via the embedded
                    browser_fallback_url. */}
                {isAndroidChrome && !twaInstalled && (
                    <motion.button
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.85 }}
                        onClick={openInApp}
                        data-testid="app-landing-open-app-fallback-link"
                        className="block mx-auto mt-4 text-[12px] text-emerald-700 font-medium underline underline-offset-4 decoration-emerald-700/40 hover:decoration-emerald-700 active:opacity-70"
                    >
                        Have the BrainSync app? Open it →
                    </motion.button>
                )}

                {embedded && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1, y: [0, 6, 0] }}
                        transition={{ opacity: { delay: 1.1, duration: 0.6 }, y: { repeat: Infinity, duration: 1.6, ease: 'easeInOut' } }}
                        className="flex flex-col items-center mt-6 text-[10px] text-gray-400 tracking-[0.15em] uppercase font-mono"
                        aria-hidden="true"
                    >
                        <span>Scroll for more</span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="mt-1">
                            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </motion.div>
                )}
            </div>

            {/* Email/password auth - opens in-place, doesn't replace the
                Google CTA. Mode (register vs login) is controlled by which
                link the user tapped. */}
            <EmailAuthSheet
                open={emailSheetOpen}
                onOpenChange={setEmailSheetOpen}
                initialMode={emailSheetMode}
                onGoogleSignIn={onSignIn}
            />
        </div>
    );
};

export default AppLanding;
